import type { User } from '@qub/base-client';
import { useState } from 'react';
import { AuthScreen, Brand } from '@/components/auth-screen';
import { ProfileDialog } from '@/components/profile-dialog';
import { SidePanel } from '@/components/side-panel';
import { TaskBoard } from '@/components/task-board';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { Alert, Avatar, Button, Icon } from '@/components/ui';
import { usePresence } from '@/hooks/use-presence';
import { useProfile } from '@/hooks/use-profile';
import { useSession } from '@/hooks/use-session';
import { qubbase } from '@/lib/qubbase';

export function App() {
  const session = useSession();
  if (session === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="size-6 animate-spin rounded-full border-2 border-brand border-t-transparent" aria-label="Loading" />
      </div>
    );
  }
  return session ? <Board key={session.user.id} user={session.user} /> : <AuthScreen />;
}

function Board({ user }: { user: User }) {
  const { profile, error, ...profileActions } = useProfile(user);
  const [profileOpen, setProfileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const presence = usePresence(profile ? { user_id: user.id, username: profile.username, avatar_path: profile.avatar_path, online_at: '' } : null);

  const signOut = async () => {
    setSigningOut(true);
    // Guests lose their account when they sign out, so tidy up their tasks first (RLS: own rows only).
    if (user.is_anonymous) await qubbase.from('tasks').delete().eq('user_id', user.id);
    await qubbase.auth.signOut();
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-line bg-surface/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Brand />
          <div className="flex items-center gap-2">
            <ThemeSwitcher />
            {profile && (
              <button onClick={() => setProfileOpen(true)} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-sunken" title="Edit profile">
                <Avatar path={profile.avatar_path} name={profile.username} size={28} />
                <span className="hidden max-w-[10rem] truncate sm:inline">{profile.username}</span>
              </button>
            )}
            <Button variant="ghost" onClick={signOut} loading={signingOut} title="Sign out">
              <Icon name="logout" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_280px]">
        {error && <Alert>Could not load your profile: {error}. Did you run the setup script (qubbase/schema.sql)?</Alert>}
        {user.is_anonymous && (
          <div className="lg:col-span-2">
            <Alert tone="info">You are browsing as a guest. Your tasks are removed when you sign out.</Alert>
          </div>
        )}
        {profile && (
          <>
            <TaskBoard userId={user.id} onTyping={presence.notifyTyping} typing={presence.typing} />
            <SidePanel online={presence.online} me={user.id} />
          </>
        )}
      </main>

      {profile && <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} user={user} profile={profile} {...profileActions} />}
    </div>
  );
}
